import { IConfiguration } from '../../domain/conf'
import { IGnsOptionsConfig } from '../../domain/conf/identity'
import { AdService } from '../../services/ads'
import { Utils } from '../../utils/common'
import { logger } from '../../utils/common/logger'
import { DOMEvents, DOMUtils } from '../../utils/dom'
import { GlobalStore } from '../../utils/storage/global'
import { Store } from '../../utils/storage/keys'
import { LocalStore } from '../../utils/storage/local'
import { AdDOM } from '../ads/ad'
import { IdentityEvent, IIdentityEvent } from '../common/events/identity'
import { IProfile, Profile } from '../common/profile'
import {
    IIdentityProviderConfig,
    IIdentityServiceAdapter,
    IIExtendedAccessUserState,
    ISubscriberProviderConfig,
    ISubscriberServiceAdapter
} from './adapters/adapter'
import { BlockerPasswordlessEmailForm } from './adapters/forms/blocker-passwordless-email-form'
import { ForgotPasswordForm } from './adapters/forms/forgot-password-form'
import { PasswordlessEmailForm } from './adapters/forms/passwordless-email-form'
import { ResetPasswordForm } from './adapters/forms/reset-password-form'
import { VerificationEmailForm } from './adapters/forms/verification-email-form'
import { VerifyAddressForm } from './adapters/forms/verify-address-form'
import { ExtendedAccessService } from './adapters/google'
import { ILRApiClientConfig, LoginService } from './adapters/loginradius/services/login-service'
import { NewsletterOptIn } from './adapters/pages/newsletter-opt-in'
import { NewspapersRedirect } from './adapters/pages/newspapers-redirect'
import { OnboardExclusiveNewsletter } from './adapters/pages/onboard-exclusive-newsletter'
import { OnboardFollowAuthor } from './adapters/pages/onboard-follow-author'
import { PasswordlessEmailSent } from './adapters/pages/passwordless-email-sent'
import { RegistrationSuccess } from './adapters/pages/registration-success'
import { VerifyPasswordless } from './adapters/pages/verify-passwordless'
import { ProfileService } from './adapters/profile-service'
import { Sophi } from './adapters/sophi'
import { Viafoura } from './adapters/viafoura'

export interface IIdentityService {
    initialize(...args: any[]): void

    update(profile: IProfile, store: boolean): void
}

export class IdentityService {
    /**
     * Required DOM selectors and attributes.
     *
     * IMPORTANT: These will be duplicated elsewhere (templates, css) so ensure changes are coordinated
     * and instances minimized.
     */
    public static DOM = {
        attrs: {
            activateButton: 'data-id-activate-button',
            btns: 'data-sso-btns',
            close: 'data-id-close',
            loginRedirect: 'data-id-login-redirect',
            marker: 'data-sso-marker',
            myAccountBtn: 'data-sso-my-account-control',
            name: 'data-id-name',
            offerId: 'data-id-offer',
            signIn: 'data-id-login',
            signOut: 'data-id-logout',
            termId: 'data-id-term'
        },
        class: {
            adContainers: ['ad__container', 'vf3-conversations-list__promo'],
            buttonText: 'button__text',
            closeStickyAd: 'close-sticky-ad',
            signedIn: 'sso-signedIn',
            signedOut: 'sso-signedOut',
            storySection: ['article-content__content-group', 'article-content__content-group--story'],
            subscriptionAlert: 'tp-container-inner',
        },
        links: {},
        logo: {},
        property: ''
    }

    public profile: IProfile
    public profileService: ProfileService
    public passwordlessEmailForm: PasswordlessEmailForm
    public blockerPasswordlessEmailForm: BlockerPasswordlessEmailForm
    public newspapersRedirect: NewspapersRedirect
    public passwordEmailSent: PasswordlessEmailSent
    public verifyAddressForm: VerifyAddressForm
    public verifyPasswordless: VerifyPasswordless
    public verificationEmailForm: VerificationEmailForm
    public newsletterOnboarding: NewsletterOptIn
    public registrationSuccess: RegistrationSuccess
    public followAuthorsOnboarding: OnboardFollowAuthor
    public onboardExclusiveNewsletter: OnboardExclusiveNewsletter
    public subscriptionServiceEnabled: boolean
    public isMeterExpired: boolean
    public loginService: LoginService
    public sophi: Sophi
    public isNavigaAuth0: boolean

    protected hashCode: number
    protected hasSubscriptions: boolean
    protected hasRegistrantTerm: boolean
    protected hasRegistrantResId: boolean
    protected subscriptionClientId: string
    protected subscriptionAppId: string
    protected subscriptionRegistrantResId: string
    protected printActivatedTermId: string

    protected termId: string
    protected offerId: string
    protected subscribeButton: HTMLButtonElement

    protected openCheckoutAfterLogin: boolean
    protected conf: IConfiguration

    private identityProvider: IIdentityServiceAdapter
    private subscriberService: ISubscriberServiceAdapter
    private commentService: Viafoura
    private extendedAccessService: ExtendedAccessService
    private userState: IIExtendedAccessUserState
    private forgotPasswordForm: ForgotPasswordForm
    private resetPasswordForm: ResetPasswordForm

    /**
     * Adds DOM event listeners.
     */
    public constructor() {
        this.subscriptionServiceEnabled = false
        // fetch profile from per-domain store, proceed anonymously if none found
        this.profile = LocalStore.get<IProfile>(Store.ID) || new Profile()

        // attempt to fetch profile from global (cross-domain) store
        GlobalStore.get<Profile>(Store.ID)
            .then((profile) => {
                // update relevant data if same user
                if (profile && profile.email === this.profile.email) {
                    this.profile.ageOfMajority = profile.ageOfMajority
                    this.profile.id = profile.id
                }
            })
            .catch(console.log)

        document.addEventListener(IdentityEvent.IDENTITY_PROFILE_INIT, async (e: IIdentityEvent) => {
            logger('debug', 'Identity profile init event, updating profile')
            await this.update(e.detail)
        })
    }

    /**
     * Stores profile; global properties only
     * @param {Profile} profile - profile to store
     */
    public static async StoreProfile(profile: IProfile) {
        // clean
        const profileStore = Utils.prune(profile)

        // store
        LocalStore.set(Store.ID, profileStore)

        // subscriptions vary by property
        delete profileStore.subscriptions
        await GlobalStore.set(Store.ID, profileStore)
    }

    /**
     * Delete subscription alert message
     *
     * @params {string} className
     */
    private static removeSubscriptionAlert(className: string) {
        if (document.body.classList.contains(className)) {
            document.querySelector(`.${IdentityService.DOM.class.subscriptionAlert}`).remove()
            document.body.classList.remove(className)

            // let other UI elements know of removal
            window.dispatchEvent(
                new CustomEvent('articleLimitAlertEvent', {
                    detail: {
                        limit: false
                    }
                })
            )
        }
    }

    /**
     * Configure Identity Service
     *
     * @param conf - Configuration object from page-data
     * @param identityProvider - Identity Provider
     * @param subscriberService - Subscriber Service
     * @param commentService - Comment Service
     * @returns
     */
    public async initialize(conf: IConfiguration,
                            identityProvider: IIdentityServiceAdapter,
                            subscriberService?: ISubscriberServiceAdapter,
                            commentService?: Viafoura) {
        this.conf = conf

        const adLightEnabled = this.conf.ads.adLightEnabled
        this.profile.adLightDisabled = !adLightEnabled
        this.isNavigaAuth0 = (this.conf.identity?.sso?.vendor === 'naviga_auth0') ? true : false

        this.identityProvider = identityProvider
        this.subscriberService = subscriberService
        this.commentService = commentService
        // We want login service to be accessible on the IdentityService instance
        // so that analytical events can be dispatched.
        // This feature is used for example by Cheetah.
        this.loginService = LoginService

        let ssoCreds: IIdentityProviderConfig
        let subscriberCreds: ISubscriberProviderConfig
        let authorizationUrl = ''
        let allowedReferrers: string[] = []
        let gnsOptions: IGnsOptionsConfig = {offerId:'', templateId:''}

        if (conf.identity) {
            ssoCreds = conf.identity.sso
            subscriberCreds = conf.identity.subscriber
            gnsOptions = conf.identity.gnsOptions
            IdentityService.DOM.links = conf.identity.links
            IdentityService.DOM.logo = conf.identity.logo
            IdentityService.DOM.property = conf.identity.property
            if (conf.identity.sso && conf.identity.sso.loginradius) {
                // Remove old lr-token from redirectUrl
                const params = new URLSearchParams(location.search)
                if (params.has('lr-token')) {
                    params.delete('lr-token')
                }
                const redirectUrl = `https://${location.host}${location.pathname}?${params.toString()}`
                // tslint:disable-next-line:max-line-length
                authorizationUrl = `https://${conf.identity.sso.loginradius.appName}.hub.loginradius.com/RequestHandler.aspx?apikey=${conf.identity.sso.loginradius.apiKey}&provider=google&same_window=&is_access_token=true&callbacktype=QueryString&disablesignup=undefined&scope=&callback=${encodeURIComponent(redirectUrl)}`

                // Current location.host must be explicitly included in the allowedReferrers list
                allowedReferrers = ['localhost',
                    `${conf.identity.sso.loginradius.appName}.hub.loginradius.com`,
                    window.location.host
                ]
            }
        }
        this.hasSubscriptions = !!subscriberCreds
        this.hasRegistrantTerm = this.hasSubscriptions ? !!subscriberCreds.registrantTermId : false
        this.subscriptionClientId = this.hasSubscriptions ? subscriberCreds.registrantTermId : ''
        this.subscriptionAppId = this.hasSubscriptions ? subscriberCreds.account : ''
        this.hasRegistrantResId = this.hasSubscriptions ? !!subscriberCreds.registrantResId : false
        this.subscriptionRegistrantResId = this.hasRegistrantResId ? subscriberCreds.registrantResId : ''

        if (this.subscriberService) {
            this.subscriptionServiceEnabled = true
        }

        this.extendedAccessService = new ExtendedAccessService(
            authorizationUrl,
            allowedReferrers,
            this.subscriberService,
            this.identityProvider,
            this.subscriptionAppId,
            gnsOptions)

        // Reload page if user just logged in and GNS url parameters missing
        // Don't reload page if the user is on sign-in page
        this.extendedAccessService.reloadIfNeeded()

        document.addEventListener(DOMEvents.PIANO_METER_EVENT, async (e: CustomEvent) => {
            this.isMeterExpired = e.detail.meterData.expired
            if (this.extendedAccessService) {
                this.userState = this.extendedAccessService.updateUserState(this.profile, this.isMeterExpired)
                // tslint:disable-next-line:max-line-length
                logger('info', `Piano meter event ${JSON.stringify(e.detail)}, userState ${JSON.stringify(this.userState)}`)
                await this.extendedAccessService.init()
            }
        })
        if (ssoCreds) {
            document.addEventListener(IdentityEvent.COMMENT_LOGIN, (e: CustomEvent) => {
                // get commentRedirect from event detail
                const commentRedirect: boolean = e.detail.commentRedirect
                const commentParam = location.search.length > 0 ? '&comments' : '?comments'
                // redirect to comment widget if needed after sign in
                const redirectUrl = commentRedirect ? location.href + commentParam : location.href
                this.identityProvider.signIn(redirectUrl)
                logger('debug', `IdentityService Event COMMENT_LOGIN; redirect: ${commentRedirect}`)
            })
            document.addEventListener(IdentityEvent.COMMENT_LOGOUT, () => {
                this.identityProvider.signOut()
                logger('debug', `IdentityService Event COMMENT_LOGOUT`)
            })
            document.addEventListener(IdentityEvent.LOGIN_MODAL_READY, this.loginModalReady.bind(this))
            document.addEventListener(IdentityEvent.IDENTITY_CHANGE, async (e: IIdentityEvent) => {
                logger('debug', 'Identity change event, updating profile')
                await this.update(e.detail)
                // update UI
                logger('debug', 'Identity change event, updating UI')
                await this.updateUI()
                if (this.extendedAccessService) {
                    this.userState = this.extendedAccessService.updateUserState(this.profile, this.isMeterExpired)
                    logger('info', `Identity change event, userState ${JSON.stringify(this.userState)}`)
                }
            })
            await this.session()
        } else {
            await this.update(this.profile)
        }

        // LoginRadius only
        if (!this.isNavigaAuth0) {
            let lrApiConfig: ILRApiClientConfig
            if (conf.identity.sso) {
                const apiKey = (conf.identity.sso.hasOwnProperty('loginradius')) ?
                    conf.identity.sso.loginradius.apiKey : ''
                const appName = (conf.identity.sso.hasOwnProperty('loginradius')) ?
                    conf.identity.sso.loginradius.appName : ''
                lrApiConfig = {
                    apiKey,
                    appName,
                    integrationName: 'news-websites',
                    objectName: 'postmedia-obj'
                }
            }

            this.profileService = new ProfileService()
            this.passwordlessEmailForm = new PasswordlessEmailForm()
            this.passwordlessEmailForm.init()
            this.blockerPasswordlessEmailForm = new BlockerPasswordlessEmailForm()
            await this.blockerPasswordlessEmailForm.init()

            this.passwordEmailSent = new PasswordlessEmailSent()
            this.passwordEmailSent.init()
            this.newsletterOnboarding = new NewsletterOptIn()
            this.newsletterOnboarding.init()
            this.registrationSuccess = new RegistrationSuccess(
                this.subscriberService,
            )
            this.registrationSuccess.init()
            this.followAuthorsOnboarding = new OnboardFollowAuthor()
            this.followAuthorsOnboarding.init()
            this.onboardExclusiveNewsletter = new OnboardExclusiveNewsletter()
            this.onboardExclusiveNewsletter.init()

            // initalize newspapers.com redirect page
            this.newspapersRedirect = new NewspapersRedirect(conf.page.siteName)
            await this.newspapersRedirect.init()

            // initialize verify passwordless page
            this.verifyPasswordless = new VerifyPasswordless()
            await this.verifyPasswordless.init(lrApiConfig)

            // initialize password reset widget
            this.resetPasswordForm = new ResetPasswordForm()
            await this.resetPasswordForm.init(lrApiConfig)

            // initialize email validation and send verification email widgets
            this.identityProvider.initializeEmailValidationWidget()
            this.verificationEmailForm = new VerificationEmailForm()
            this.verificationEmailForm.init()

            // initialize forgot password form
            this.forgotPasswordForm = new ForgotPasswordForm()
            this.forgotPasswordForm.init()
        }

        if (this.hasSubscriptions) {
            this.printActivatedTermId = (subscriberCreds) ? subscriberCreds.activateTermId : ''
            logger('debug', 'IdentityService.initialize - property has subscriptions, updating subscriptions')
            await this.updateSubscriptions()
            logger('debug', 'IdentityService.initialize - updating conversions')
            await this.updateConversions()
            this.sophi = new Sophi(this.subscriberService, conf.page,
                conf.identity.sophiPaywallOffer, conf.identity.useSophiDecision,
                this.extendedAccessService.isGnsEnabled(location.href))
            await this.sophi.init()
            logger('debug', 'IdentityService.initialize - adding Sophi')
            this.verifyAddressForm = new VerifyAddressForm(subscriberService)
            await this.verifyAddressForm.init()
            logger('debug', 'VerifyAddressForm.initialize - adding form validation event listener')
        }

        // fire id init event
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_INIT, this.profile))
        await this.loginModalReady()
        logger('debug', 'IdentityService.initialize - updating UI')
        await this.updateUI()

        // Start GNS services when userstate is set and scripts are loaded
        logger('info', `Identity init event, userState ${JSON.stringify(this.extendedAccessService.readUserState())}`)
        return this
    }

    public getProfile(): IProfile {
        return this.profile
    }

    public async update(data: IProfile) {
        logger('debug', 'IdentityService.update with incoming profile: ', data)
        // outdated data, exit
        if (data.updated && this.profile && data.updated < this.profile.updated) {
            logger('debug', 'IdentityService.update - outdated data, exiting')
            return
        }

        const newLogin = this.profile && (this.profile.email !== data.email)
        logger('debug', 'IdentityService.update - newLogin is ' + newLogin)

        if (newLogin) {
            this.profile = {...this.profile, conversions: undefined, subscriptions: undefined}
            logger('debug', 'IdentityService.update - conversions and subscriptions cleared')
        }

        // merge data
        this.profile = {...this.profile, ...data}
        const incomingHashCode = Utils.hashCode(Utils.prune(this.profile))

        // profile unchanged
        if (this.hashCode && (incomingHashCode === this.hashCode)) {
            logger('debug', 'IdentityService.update - profile unchanged, exiting')
            return
        }

        // update modified time
        if (this.hashCode || !this.profile.updated) {
            this.profile.updated = Date.now()
        }
        // update hash (need to set before async actions so can ignore when no changes)
        this.hashCode = incomingHashCode
        if (this.hasSubscriptions) {
            logger('debug', 'IdentityService.update - property has subscriptions, updating subscriptions')
            // get subscriptions
            await this.updateSubscriptions()
            logger('debug', 'IdentityService.update - subscriptions updated')

            // check for one-digital conversions (only once to avoid potential race condition adding double terms)
            if (newLogin) {
                logger('debug', 'IdentityService.update - new login, updating conversions')
                await this.updateConversions()
                logger('debug', 'IdentityService.update - conversions updated')
            }

            // update print activated status
            this.updatePrintSubscriber()
        }

        // update hash
        this.profile.updated = Date.now()
        this.hashCode = Utils.hashCode(Utils.prune(this.profile))

        if (this.profile.email && newLogin) {
            document.dispatchEvent(
                new CustomEvent(IdentityEvent.NEW_LOGIN, {
                    detail: {
                        data: this.profile
                    }
                })
            )
        }
        const pianoParams = LoginService.getPianoParams()
        if ((this.openCheckoutAfterLogin || pianoParams) && this.offerId && this.termId) {
            this.subscriberService.showCheckoutModal(this.offerId, this.termId, this.subscribeButton)
            this.openCheckoutAfterLogin = false
        }

        // store
        await IdentityService.StoreProfile(this.profile)

        Utils.dispatchEventWithFemFlagSet(IdentityEvent.IDENTITY_READY, {detail: this.profile})
    }

    /**
     * Enable sign-in link when adaptor is ready
     */
    public async loginModalReady() {
        if (this.isNavigaAuth0) {
            return
        }

        await DOMUtils.ready

        /* register listeners only when janrain finished loading */
        this.addListeners()

        /* remove sign in loader */
        const signInEl: NodeListOf<HTMLButtonElement> = document.querySelectorAll(
            `[${IdentityService.DOM.attrs.signIn}]`
        )
        if (signInEl.length && signInEl.length > 0) {
            signInEl[0].removeAttribute('style')
            signInEl[0].classList.add('signin-button--modal-ready')
            signInEl[0].setAttribute('aria-disabled', 'false')
            signInEl[0].disabled = false
            const loaderDiv = document.getElementsByClassName('signInLoader')
            if (loaderDiv.length && loaderDiv.length > 0) {
                signInEl[0].removeChild(loaderDiv[0])
            }
        }
    }

    /**
     * Check if subscribed to the property (Not cross-app access)
     */
    public subscribed() {
        const propertySubscriber = (this.profile.subscriptions || []).filter((subscription) => {
            return subscription.aid === this.subscriptionAppId && subscription.id !== this.subscriptionRegistrantResId
        })
        return propertySubscriber.length > 0
    }

    /**
     * Adds the print subscriber status to the profile
     */
    public updatePrintSubscriber() {
        const printSubscriber = (this.profile.conversions || []).filter((conversion) => {
            return conversion === this.printActivatedTermId
        })
        this.profile.printSubscriber = printSubscriber.length > 0
        logger('info', 'Print Subscriber: ', this.profile.printSubscriber)
    }

    /**
     * Updates UI (buttons etc.). Called after state change etc.
     */
    public async updateUI() {
        if (this.isNavigaAuth0) {
            return
        }

        await DOMUtils.ready
        logger('info', 'updating UI')

        // adLight can be disabled on a site-by-site basis
        const adLight = this.profile.adLight && !this.profile.adLightDisabled
        const subscribed = this.subscribed()

        logger('info', 'subscribed: ', subscribed)

        if (adLight) {
            this.commentService.removeCommentWidgetsAdUI()
            this.removeAdSlotElements()
        }

        this.setName(this.profile.email ? this.profile.name : undefined).catch(console.log)

        if (this.signedIn()) {
            // Remove the article counter
            if (adLight || subscribed) {
                IdentityService.removeSubscriptionAlert('article-counter--visible')
            }

            // Remove the register counter
            IdentityService.removeSubscriptionAlert('register-counter--visible')

            // Remove register hard gate.
            // The .register-modal--visible class is coming from Piano admin panel.
            // Don't need to check if subscription services is enabled or not
            if (document.body.classList.contains('register-modal--visible')) {
                document.body.classList.remove('register-modal--visible')
                this.subscriberService.closeCheckoutModal()
            }

            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signIn}]`), -1)
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signOut}]`))

            // signed in unhide the my account element from DOM & AT
            const myAccountBtn = document.querySelector<HTMLButtonElement>(
                `[${IdentityService.DOM.attrs.myAccountBtn}]`)
            if (myAccountBtn) { myAccountBtn.hidden = false }

            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.myAccountBtn}]`))
        } else {
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signIn}]`))
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signOut}]`), -1)
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.myAccountBtn}]`), -1)
        }
        if (document.querySelectorAll(`[${IdentityService.DOM.attrs.marker}]`).length) {
            // find DOM items based on marker specified above
            // replace class show/hide for animation rather than display block/none
            DOMUtils.nodes(document.querySelectorAll(`[${IdentityService.DOM.attrs.marker}], body`))
                .forEach((el: Element) => {
                    // replace signed in classes based on status
                    if (!this.signedIn()) {
                        DOMUtils.replaceClass(el,
                            IdentityService.DOM.class.signedIn,
                            IdentityService.DOM.class.signedOut)
                    } else if (!!this.signedIn()) {
                        DOMUtils.replaceClass(el,
                            IdentityService.DOM.class.signedOut,
                            IdentityService.DOM.class.signedIn)
                    }
                })
        } else {
            // display block/none method currently used
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.signIn}]`),
                !this.signedIn())
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.signOut}]`),
                !!this.signedIn())
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.myAccountBtn}]`),
                !!this.signedIn())
        }
    }

    /**
     * Handles session init event from SSO provider.
     */
    protected async session() {
        const profile = this.profile
        logger('info', 'identity session called with profile:', profile)

        if (!this.isNavigaAuth0) {
            // tslint:disable-next-line:max-line-length
            await this.commentService.initialize(this.conf.ads, this.identityProvider.getAccessToken())
        }
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_SESSION_PROFILE_INIT, profile))
        await this.update(profile)
    }

    /**
     * Registers listeners for required DOM events.
     *
     * TODO: expand as implementation requires.
     * @TODO: rename this
     */
    private addListeners() {
        // #if(document){ return }
        document.addEventListener('click', async (e: Event) => {
            let el = e.target as Element

            if (el.classList.contains(IdentityService.DOM.class.buttonText)) {
                // move up to the button element if clicking on the button text
                el = el.parentElement
            }

            const lrTokenKey = localStorage.getItem(Store.LR_TOKEN_KEY)

            let redirectUrl
            if (el.getAttribute(IdentityService.DOM.attrs.loginRedirect)) {
                redirectUrl = (el.getAttribute(IdentityService.DOM.attrs.loginRedirect).indexOf('http') !== -1 ?
                     '' : location.origin) + el.getAttribute(IdentityService.DOM.attrs.loginRedirect)
            } else {
                redirectUrl = location.origin
            }

            // Use "data-id-login-redirect" to make custom redirect from button
            if (el.hasAttribute(IdentityService.DOM.attrs.loginRedirect) && !lrTokenKey) {
                this.identityProvider.signIn(redirectUrl)
            } else if (el.hasAttribute(IdentityService.DOM.attrs.loginRedirect) && lrTokenKey) {
                location.assign(redirectUrl)
            }

            if (el.hasAttribute(IdentityService.DOM.attrs.signIn)) {
                this.openCheckoutAfterLogin = false
                this.identityProvider.signIn()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.signOut)) {
                if (this.commentService) {
                    logger('info', 'identity sign out called - signing out vf widget')
                    await this.commentService.doWidgetSignout()
                    logger('info', 'widget signed out successfully')
                }
                logger('info', 'identity sign out called - signing out FEM')
                this.identityProvider.signOut()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.offerId) && this.subscriberService) {
                this.handleOfferButton(el as HTMLButtonElement)
            } else if (el.hasAttribute(IdentityService.DOM.attrs.activateButton) && this.subscriberService) {
                e.preventDefault()
                this.handleActivateButton()
            }
        })
    }

    /**
     * Offer button interactions
     *
     * @param {HTMLButtonElement} button
     */
    private handleOfferButton(button: HTMLButtonElement) {
        this.offerId = button.getAttribute(IdentityService.DOM.attrs.offerId)
        this.termId = button.getAttribute(IdentityService.DOM.attrs.termId)
        if (this.offerId && this.termId) {
            this.subscriberService.showCheckoutModal(this.offerId, this.termId, button)
        }
    }

    /**
     * Activate button interactions
     */
    private handleActivateButton() {
        const profile = this.profile

        // Already activated
        if (profile && profile.printSubscriber) {
            location.assign('/already-activated/')
            return
        }

        // Signed in but not activated
        if (this.signedIn()) {
            location.assign('/verify-delivery-address/')
            return
        }

        // Not signed in and not activated
        if (sessionStorage) {
            sessionStorage.setItem(Store.ACTIVATE_BUTTON, 'true')
            LoginService.setRedirectUrl(`${location.origin}/verify-delivery-address/`)
        }
        location.assign('/register/')
    }

    /**
     * Sets name in UI (based on attr). Hides for anonymous users.
     *
     * @param {string} name
     */
    private async setName(name?: string) {
        await DOMUtils.ready
        DOMUtils.nodes(document.querySelectorAll(`[${IdentityService.DOM.attrs.name}]`))
            .forEach((el: Element) => {
                DOMUtils.setVis(el, !!name)
                el.innerHTML = name
            })
    }

    /**
     * If one digital, checks to see if user needs to be added to registrant term.
     */
    private async updateConversions() {
        if (this.profile.email) {
            if (this.profile.conversions === undefined && this.hasRegistrantTerm) {
                const conversions = await this.subscriberService.getConversions()
                this.profile.conversions = conversions
                logger('debug', 'IdentityService.updateConversions - conversions:', conversions)

                if (this.profile.conversions && this.profile.conversions.indexOf(this.subscriptionClientId) === -1) {
                    // no record of having registered this user for basic digital access on this one-digital property
                    // tslint:disable-next-line: max-line-length
                    logger('debug', 'IdentityService.updateConversions - registering for One digital access')
                    // tslint:disable-next-line: max-line-length
                    await this.subscriberService.registerForOneDigital(this.conf.identity.subscriber.account)
                    // tslint:disable-next-line: max-line-length
                    logger('debug', 'IdentityService.updateConversions - registered for One digital access')
                    this.profile.conversions = [...this.profile.conversions, this.subscriptionClientId]
                }
            }
        } else {
            logger('debug', 'IdentityService.updateConversions - no email, not updating conversions')
            this.profile.conversions = undefined
        }
    }

    /**
     * Gets subscriptions.
     */
    private async updateSubscriptions() {
        if (this.profile.email) {
            if (this.profile.subscriptions === undefined && this.hasSubscriptions) {
                // direct assignment causes issues in Mac FF/Saf (others?):
                // this.profile.subscriptions = await this.adapter.getAccess()

                // ...but intermediate assignment works:
                const access = await this.subscriberService.getAccess()
                this.profile.subscriptions = access
                logger('debug', 'IdentityService - updateSubscriptions - Subscriptions: ', access)
            }
        } else {
            logger('debug', 'IdentityService - updateSubscriptions: no email, not updating subscriptions')
            this.profile.subscriptions = undefined
        }
    }

    /**
     * Check if user is currently signed in
     */
    private signedIn() {
        return this.profile.email ? true : undefined
    }

    /**
     * Retrieve all ad slots by the container from DOM and delete
     * @TODO move this to viafoura object
     */
    private removeAdSlotElements() {
        // Use requestAnimationFrame to ensure DOM is ready, then use MutationObserver
        // to catch ad slots as they're added to the DOM
        requestAnimationFrame(() => {
            this.removeExistingAdSlots()
            this.observeAndRemoveNewAdSlots()
        })
    }

    private removeExistingAdSlots() {
        for (const adContainer of IdentityService.DOM.class.adContainers) {
            const adSlots = Array.from(document.querySelectorAll(`.${adContainer}`))
                .filter((el: Element) =>
                    !el.querySelector(`[${AdDOM.attr.adLightIneligible.key}]`)
                )
            adSlots.forEach((el: Element, index: number) => {
                // Always replace ad slot, but skip adding dfpAdSpacer on first ad
                const isFirstAd = index === 0
                this.replaceAdSlotWithSpacer(el, isFirstAd)
            })
        }
        this.cleanupEmptyElements()
        this.removeUnnecessaryButtons()
    }

    /**
     * Replace ad slot with spacer element
     */
    private replaceAdSlotWithSpacer(adElement: Element, isFirstAd: boolean) {
        const parent = adElement.parentElement?.parentElement
        if (parent) {
            const adEmpty = document.createElement('div')

            // Only add dfpAdSpacer if not the first ad
            if (!isFirstAd) {
                adEmpty.classList.add(...AdService.DOM.classes.dfpAdSpacer)
            }
            parent.replaceWith(adEmpty)
        }
    }

    /**
     * Clean up empty elements after ad removal
     */
    private cleanupEmptyElements() {
        // Remove unnecessary spacing around paragraphs after ad slots are removed
        const storySectionClasses = `.${IdentityService.DOM.class.storySection.join('.')}`
        for (const articleSection of document.querySelectorAll(storySectionClasses)) {
            const firstP = articleSection.querySelector('p')
            if (firstP && firstP.innerHTML.trim() === '') {
                firstP.remove()
            }
        }
        // Fix unnecessary whitespace when trending widget anchors to in-article widget
        this.removeEmptySectionUnderWidget()
    }

    /**
     * Remove unnecessary UI buttons
     */
    private removeUnnecessaryButtons() {
        const closeButton = document.querySelector(`.${IdentityService.DOM.class.closeStickyAd}`)
        closeButton?.remove()
    }

    /**
     * Observes and removes new ad slots as they are added to the DOM
     */
    private observeAndRemoveNewAdSlots() {
        // Check if MutationObserver is available (not available in some test environments)
        if (typeof MutationObserver === 'undefined') {
            return
        }

        // Stop observing after 5 seconds to avoid performance issues
        const OBSERVER_TIMEOUT_MS = 5000
        // Prevent infinite loops
        const MAX_ITERATIONS = 100
        let iterationCount = 0

        const observer = new MutationObserver((mutations) => {
            if (iterationCount++ > MAX_ITERATIONS) {
                observer.disconnect()
                return
            }
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        this.processNewElement(node as Element)
                    }
                })
            })
        })
        // Observe the entire document for ad slot additions
        observer.observe(document.body, {
            childList: true,
            subtree: true
        })
        setTimeout(() => observer.disconnect(), OBSERVER_TIMEOUT_MS)
    }

    /**
     * Process newly added DOM elements for ad removal
     */
    private processNewElement(element: Element) {
        const adContainerSelectors = IdentityService.DOM.class.adContainers
            .map((cls) => `.${cls}`)
            .join(', ')

        const adSlots = [
            ...(element.matches?.(adContainerSelectors) ? [element] : []),
            ...Array.from(element.querySelectorAll?.(adContainerSelectors) || [])
        ].filter((el: Element) =>
            !el.querySelector(`[${AdDOM.attr.adLightIneligible.key}]`)
        )

        adSlots.forEach((el: Element) => {
            this.replaceAdSlotWithSpacer(el, false)
        })
    }

    private removeEmptySectionUnderWidget() {
        const widgetElement = document.querySelector('.js-widget-content') as HTMLElement
        if (widgetElement) {
            widgetElement.style.marginBottom = '40px'

            // Get the next sibling element
            let siblingElement = widgetElement.nextElementSibling as HTMLElement

            // Loop until you find a section tag
            const maxIter = 10  // limit iterations for safety
            let curIter = 0
            while (siblingElement && siblingElement.tagName.toLowerCase() !== 'section' && curIter < maxIter) {
                siblingElement = siblingElement.nextElementSibling as HTMLElement
                curIter += 1
            }
            if (siblingElement && siblingElement.tagName.toLowerCase() === 'section') {
                // check if there is any visible text content
                const text = siblingElement.innerText.trim()
                if (text === '' || text === 'Article content') {
                    siblingElement.remove()
                }
            }
        }
    }
}
